import { FormEvent, useEffect, useRef, useState } from "react";
import { MerChantModel } from "../../../models/merChantModel";
import axios from "../../../lib/axios";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import {
  MERCHANT_STORE_FIND_ONE,
  MERCHANT_STORE_UPDATE_LOCATION,
  MERCHANT_STORE_UPDATE,
} from "../../../lib/endpoints/merchant";
import { IMAGE_MERCHANT_URL } from "../../../constants/imageUrl";
import { useTranslation } from "react-i18next";
import { generateImageFileNameWithRandomName } from "../../../utils/generateImageFileName";
import { DeliveryFeeModel } from "../../../models/deliveryFee";
import { DELIVERY_FEE_FIND_ONE } from "../../../lib/endpoints/deliveryFee";
import { MerchantUserModel } from "../../../models/merchantUser";

const useMerchantDetailController = () => {
  const { t } = useTranslation("global");
  const authData: MerchantUserModel = useSelector(
    (state: RootState) => state?.auth?.data ?? null!
  );

  const [imageBannerToShow, setImageBannerToShow] = useState<any>(null!);
  const [merchantData, setMerchantData] = useState<MerChantModel>(null!);
  const [imageToShow, setImageToShow] = useState<any>(null!);
  const [imageFile, setImageFile] = useState<any>(null!);
  const [errorMessage, setErrorMessage] = useState<string>(null!);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [loadingConfirmButton, setLoadingConfirmButton] =
    useState<boolean>(false);
  const [deliveryFee, setDeliveryFee] = useState<string>("");
  const nameLa = useRef<HTMLInputElement>(null!);
  const nameEn = useRef<HTMLInputElement>(null!);
  const merchantCode = useRef<HTMLInputElement>(null!);
  const merchantNo = useRef<HTMLInputElement>(null!);
  const abbName = useRef<HTMLInputElement>(null!);
  const latitude = useRef<HTMLInputElement>(null!);
  const longitude = useRef<HTMLInputElement>(null!);
  const province = useRef<HTMLInputElement>(null!);
  const district = useRef<HTMLInputElement>(null!);
  const village = useRef<HTMLInputElement>(null!);
  const phone = useRef<HTMLInputElement>(null!);

  const merchantId = useSelector(
    (state: RootState) => state?.auth?.data?.merchantId
  );

  const handleGetMerchant = async () => {
    try {
      setLoadingData(true);
      const getMerchantStore = await axios.get(
        MERCHANT_STORE_FIND_ONE + `${authData?.merchantId}`
      );
      console.log("getMerchantStore=", getMerchantStore);
      setMerchantData(getMerchantStore?.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingData(false);
    }
  };

  const handleCloseConfirmForm = () => {
    setOpenConfirm(false);
  };

  const handleOpenConfirmForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (handleValidate()) {
      setOpenConfirm(true);
    }
  };

  const handleValidate = () => {
    if (!merchantData && !imageFile) {
      setErrorMessage("Please select an image.");
      return false;
    }
    setErrorMessage("");
    return true;
  };

  const handleSubmit = () => {
    handleUpdateLocation();
    handleUpdateMerchant();
  };

  const handleUpdateMerchant = async () => {
    try {
      setLoadingConfirmButton(true);
      const formData: any = new FormData();

      if (nameEn.current?.value) {
        formData.append("nameEn", nameEn.current.value);
      }

      if (nameLa.current?.value) {
        formData.append("nameLa", nameLa.current.value);
      }

      if (abbName.current?.value) {
        formData.append("abbreviateName", abbName.current.value);
      }

      if (province.current?.value) {
        formData.append("provice", province.current.value);
      }

      if (district.current?.value) {
        formData.append("district", district.current.value);
      }

      if (village.current?.value) {
        formData.append("village", village.current.value);
      }

      if (phone.current?.value) {
        formData.append("phone", phone.current.value);
      }

      formData.append("merchantNo", merchantData?.merchantNo);
      formData.append("merchantCode", merchantData?.merchantCode);
      formData.append("company", merchantData?.company);
      formData.append("isActive", merchantData?.isActive);

      if (imageFile) {
        formData.append(
          "image",
          generateImageFileNameWithRandomName(imageFile)
        );
      }

      const res = await axios.patch(
        MERCHANT_STORE_UPDATE + merchantId,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("status == ", res);

      await handleGetMerchant();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        console.log("getError == ", getErrorMessage);
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t("Server error please try again") || "");
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirm(false);
    }
  };

  const handleUpdateLocation = async () => {
    try {
      setLoadingConfirmButton(true);
      await axios.post(
        `${MERCHANT_STORE_UPDATE_LOCATION + merchantId}&logtitude=${
          longitude.current.value
        }&latitude=${latitude.current.value}`,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "multipart/form-data",
          },
        }
      );
      await handleGetMerchant();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        console.log("getError == ", getErrorMessage);
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t("Server error please try again") || "");
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirm(false);
    }
  };

  const handleSelectImage = (e: any) => {
    const file = e.target.files[0];
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        setImageToShow(reader.result);
      }
    };
    reader.readAsDataURL(file);
    setImageToShow(file);
  };

  useEffect(() => {
    handleGetMerchant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    handleSelectImage,
    imageToShow,
    loadingData,
    nameLa,
    nameEn,
    abbName,
    latitude,
    longitude,
    province,
    district,
    village,
    phone,
    handleCloseConfirmForm,
    handleOpenConfirmForm,
    handleSubmit,
    openConfirm,
    errorMessage,
    loadingConfirmButton,
    imageBannerToShow,
    merchantData,
    deliveryFee,
  };
};
export default useMerchantDetailController;
